import React, { useContext, useMemo } from 'react'
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import { DropdownOption } from 'types/DesignSystem/Dropdown';
import { ProductsContext } from '../../ProductsContext';

interface CategoryDropDownProps {
    value: string | null | undefined,
    /** The chosen category id. Was a `ChangeEvent<HTMLSelectElement>` while this was a native select. */
    handleSelect: (categoryId: string) => void
}

/**
 * Step 1's (To) Target picker, on DS `Dropdown` since 2026-09-13.
 *
 * It was a Bootstrap `.form-select` — a native `<select>` with an `<optgroup>` per
 * category type. Bootstrap bakes `background-color:#fff` on that class, which is why
 * it stayed a white slab on the dark page even after the flow was re-inked.
 *
 * **Two rules the markup carried, both preserved:**
 *   - categories are GROUPED by their type. `DropdownOption.group` draws exactly that,
 *     and is order-driven, so the options are emitted type-by-type as before.
 *   - the current SOURCE category is present but not choosable — you cannot move
 *     products into the category they are already in. That needed `DropdownOption
 *     .disabled`, which `Dropdown` did not have; it was added for this (design-system
 *     § 284). Omitting the option instead would enforce the rule while hiding it.
 *
 * `prodContext.categoryData` is read WITHOUT optional chaining, deliberately and as
 * before: `okf/products/e2e.md` records the crash-on-undefined as a known risk and a
 * test pins it, so silently swallowing it here would erase a documented finding rather
 * than fix it.
 */
const CategoryDropDown = ({ value, handleSelect }: CategoryDropDownProps) => {
    const prodContext = useContext<any>(ProductsContext);

    const options = useMemo<DropdownOption[]>(() => (
        prodContext.categoryData.flatMap((item: any) =>
            (item.category ?? []).map((category: any) => ({
                value: category.id,
                label: category.name,
                group: item.name,
                disabled: prodContext?.selected_Category?.id === category.id,
            }))
        )
    ), [prodContext.categoryData, prodContext?.selected_Category?.id]);

    return (
        /* `w-full`, not `flex`: as a flex item the field shrink-wrapped to its label and
           rendered narrower than the two controls above it in the step. */
        <div className='tw-w-full'>
            <Dropdown
                label=""
                options={options}
                value={value ?? undefined}
                onChange={(categoryId) => handleSelect(String(categoryId))}
                placeholder="Select Target Category"
                /* The picker sits inside the modal's scrolling body; portal so the list
                   is not clipped by it. */
                usePortal
                /* QA, 2026-10-05: a filter box (`…-search`) above the list. Client-side —
                   categoryData is already loaded. Groups are built from what the filter
                   leaves, so a type with no match drops out; the source stays disabled. */
                searchable
                searchPlaceholder="Search categories"
                data-testid="recategorize-category-select"
            />
        </div>
    )
}

export default CategoryDropDown
